import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../redux/slices/authSlice";
import { clearQrCode } from "@/redux/slices/qrSlice";
import { ArrowLeft, Home, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getT } from "@/i18n/translations";
import { fetchCustomerData } from "@/redux/slices/customerSlice";


export default function Profile() {
  const t = getT();
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const mobile = localStorage.getItem("mobile") || localStorage.getItem("phone") || localStorage.getItem("phoneNumber") || "";
  const { data: customer } = useSelector((state) => state.customer);
  const customerName = customer?.customer?.name || "";
  const customerState = customer?.customer?.state || "";
  const customerDistrict = customer?.customer?.district || "";
  const customerVillage = customer?.customer?.village || "";
  const customerPincode = customer?.customer?.pin || "";
  const customerAcreage = customer?.customer?.landowned || "";


  useEffect(() => {
    const customerCode = localStorage.getItem("customercode");
    dispatch(fetchCustomerData(customerCode));
  }, []);

  const avatar = customerName ? customerName[0].toUpperCase() : mobile ? mobile[0] : "U";

  return (
    <div
      className="min-h-screen flex flex-col items-center"
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: "#fff5f5",
        backgroundImage:
          "radial-gradient(circle, #ffd6d6 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }}
    >
      <div className="w-full max-w-md min-h-screen flex flex-col bg-[#ffe5e5]">

        {/* HEADER */}
        <div className="relative flex items-center justify-center px-4 py-4 mt-2 ">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 w-9 h-9 flex items-center justify-center rounded-full bg-red-50 shadow-sm border border-red-100"
          >
            <ArrowLeft className="w-5 h-5 text-[#e60012]" />
          </button>

          <p className="text-[15px] font-bold text-[#e60012] tracking-wide">
            {t.profile_myProfile}
          </p>

          <button
            onClick={() => navigate("/home")}
            className="absolute right-4 w-9 h-9 flex items-center justify-center rounded-full bg-red-50 shadow-sm border border-red-100"
          >
            <Home className="w-5 h-5 text-[#e60012]" />
          </button>
        </div>

        {/* LOGOUT MODAL */}
        {open && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4 backdrop-blur-sm">
            <div className="bg-white w-full max-w-sm rounded-3xl p-7 text-center shadow-2xl mx-4 border border-red-100">

              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">👋</span>
              </div>

              <h2 className="text-[20px] font-bold text-[#b3000f] mb-2">
                {t.profile_logoutTitle}
              </h2>

              <p className="text-[13px] text-gray-500 mb-6 leading-relaxed">
                {t.profile_logoutMsg}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setOpen(false)}
                  className="flex-1 py-3 rounded-xl text-[14px] font-semibold border border-red-200 text-[#b3000f] bg-red-50 active:scale-[0.98]"
                >
                  {t.profile_cancel}
                </button>

                <button
                  onClick={() => {
                    dispatch(clearQrCode());
                    dispatch(logout());
                  }}
                  className="flex-1 py-3 rounded-xl text-[14px] font-semibold text-white active:scale-[0.98] shadow-sm"
                  style={{
                    background:
                      "linear-gradient(135deg, #e60012 0%, #b3000f 100%)",
                  }}
                >
                  {t.profile_logoutConfirm}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CONTENT */}
        <div className="px-4 pb-8 space-y-4 flex-1">

          {/* PROFILE CARD */}
          <div className="bg-white rounded-3xl px-5 py-5 shadow-md mx-1 flex items-center gap-4 border border-red-100">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-[22px] font-bold text-white shrink-0 shadow-sm"
              style={{
                background:
                  "linear-gradient(135deg, #e60012 0%, #b3000f 100%)",
              }}
            >
              {avatar}
            </div>

            <div>
              <p className="text-[16px] font-bold text-[#b3000f]">
                {customerName || "—"}
              </p>

              <p className="text-[13px] text-gray-400">
                {mobile || "—"}
              </p>
            </div>
          </div>

          {/* LOCATION DETAILS */}
          <div className="bg-white rounded-3xl px-5 py-4 shadow-md mx-1 border border-red-100">
            <p className="text-[10px] font-semibold text-red-400 uppercase tracking-widest mb-4">
              {t.profile_locationDetails}
            </p>

            <div className="space-y-3">
              {[
                { label: t.profile_state, value: customerState },
                { label: t.profile_district, value: customerDistrict },
                { label: t.profile_village, value: customerVillage },
                { label: t.profile_pincode, value: customerPincode },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  className="flex justify-between items-center border-b border-red-50 pb-2 last:border-0"
                >
                  <p className="text-[13px] text-gray-400">{label}</p>

                  <p className="text-[13px] font-semibold text-[#b3000f]">
                    {value || "—"}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* FARM DETAILS */}
          <div className="bg-white rounded-3xl px-5 py-4 shadow-md mx-1 border border-red-100">
            <p className="text-[10px] font-semibold text-red-400 uppercase tracking-widest mb-4">
              {t.profile_farmDetails}
            </p>

            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#e60012"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>

                <p className="text-[13px] text-gray-400">
                  {t.profile_ageLabel}
                </p>
              </div>

              <p className="text-[13px] font-semibold text-[#b3000f]">
                {customerAcreage ? `${customerAcreage}` : "—"}
              </p>
            </div>
          </div>

          {/* NOTICE */}
          <div className="rounded-2xl px-4 py-3 mx-1 flex items-start gap-2 bg-red-50 border border-red-100">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#e60012"
              strokeWidth="2"
              className="mt-0.5 shrink-0"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>

            <p className="text-[11px] text-[#b3000f] leading-relaxed">
              {t.profile_noticeText}
            </p>
          </div>

          {/* DASHBOARD BUTTON */}
          <button
            onClick={() => navigate("/home")}
            className="w-full py-4 rounded-2xl text-[15px] font-bold text-white active:scale-[0.98] transition-transform shadow-md"
            style={{
              width: "calc(100% - 8px)",
              marginLeft: "4px",
              background:
                "linear-gradient(135deg, #e60012 0%, #b3000f 100%)",
            }}
          >
            {t.profile_backToDashboard}
          </button>

          {/* LOGOUT BUTTON */}
          <button
            onClick={() => setOpen(true)}
            className="w-full py-3.5 rounded-2xl text-[14px] font-semibold text-[#b3000f] border border-red-200 bg-white active:scale-[0.98] transition-transform shadow-sm"
            style={{
              width: "calc(100% - 8px)",
              marginLeft: "4px",
            }}
          >
            {t.profile_logout}
          </button>
        </div>

        {/* FOOTER */}
        {/* <div className="w-full py-3 border-t">
          <p className="text-[13px] text-red-600 uppercase text-center tracking-wide font-semibold">
            © {new Date().getFullYear()} Consumer Journeys. All Rights Reserved.
          </p>
        </div> */}
      </div>
    </div>
  );
}